<template>
  <div class="quote-container">
    <!-- Header Section -->
    <div class="quote-header">
      <div
          v-if="settings.logo_url"
          class="tw-mb-6"
      >
        <img
            :src="settings.logo_url"
            alt="Company Logo"
            class="tw-h-12 tw-object-contain"
        >
      </div>
      <div class="tw-border-b tw-border-gray-200 tw-pb-4">
        <h1 class="tw-text-2xl tw-font-light tw-text-gray-800">
          Your Quote Summary
        </h1>
        <p class="tw-text-sm tw-text-gray-500 tw-mt-1">
          {{ quoteItems.length }} {{ quoteItems.length === 1 ? 'item' : 'items' }} in your quote
        </p>
      </div>
    </div>

    <!-- Empty State -->
    <div
        v-if="quoteItems.length === 0"
        class="tw-text-center tw-py-12"
    >
      <svg
          class="tw-mx-auto tw-h-12 tw-w-12 tw-text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
      >
        <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.5"
            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17M17 13v6m0 0v2m0-2h2m-2 0h-2"
        />
      </svg>
      <h3 class="tw-mt-4 tw-text-lg tw-font-medium tw-text-gray-900">
        Your quote is empty
      </h3>
      <p class="tw-mt-2 tw-text-sm tw-text-gray-500">
        Add products to get started with your quote.
      </p>
      <button
          class="tw-mt-6 tw-inline-flex tw-items-center tw-px-6 tw-py-3 tw-border tw-border-transparent tw-text-base tw-font-medium tw-rounded-lg tw-text-white tw-bg-green-600 hover:tw-bg-green-700 tw-transition-colors"
          @click="$emit('add-another-product')"
      >
        <svg
            class="tw-w-5 tw-h-5 tw-mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
        >
          <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
          />
        </svg>
        Browse Products
      </button>
    </div>

    <!-- Quote Items List -->
    <div
        v-else
        class="quote-items"
    >
      <div
          v-for="(item, index) in quoteItems"
          :key="item.id"
          class="quote-item"
      >
        <div class="tw-flex tw-gap-6">
          <!-- Product Image -->
          <div class="item-image-container">
            <ImageLayeringWithProps
                :images="item.images"
                class="item-image"
            />
          </div>

          <!-- Product Details -->
          <div class="tw-flex-1">
            <div class="tw-flex tw-justify-between tw-items-start">
              <div>
                <h3 class="tw-text-lg tw-font-medium tw-text-gray-900">
                  {{ item.name }}
                </h3>
                <div class="tw-mt-2 tw-space-y-1">
                  <p class="tw-text-sm tw-text-gray-600">
                    <span class="tw-font-medium">Quantity:</span> {{ item.quantity }}
                  </p>
                  <p
                      v-if="item.price"
                      class="tw-text-sm tw-text-gray-900 tw-font-medium"
                  >
                    {{ formatPrice(item.price) }}
                  </p>
                </div>
                <div
                    v-if="item.description"
                    class="tw-mt-3"
                >
                  <p
                      class="tw-text-sm tw-text-gray-500 tw-leading-relaxed"
                      v-html="item.description"
                  />
                </div>
              </div>

              <!-- Remove Button -->
              <button
                  class="tw-ml-4 tw-text-gray-400 hover:tw-text-red-500 tw-transition-colors"
                  aria-label="Remove item"
                  @click="removeItem(index)"
              >
                <svg
                    class="tw-h-5 tw-w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                  <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Section -->
      <div class="quote-summary">
        <div class="tw-bg-gray-50 tw-rounded-lg tw-p-6">
          <div class="tw-flex tw-justify-between tw-items-center tw-mb-4">
            <span class="tw-text-base tw-font-medium tw-text-gray-900">Total Items</span>
            <span class="tw-text-lg tw-font-semibold tw-text-gray-900">{{ totalQuantity }}</span>
          </div>
          <div
              v-if="totalPrice"
              class="tw-flex tw-justify-between tw-items-center tw-pt-4 tw-border-t tw-border-gray-200"
          >
            <span class="tw-text-base tw-font-medium tw-text-gray-900">Estimated Total</span>
            <span class="tw-text-xl tw-font-bold tw-text-gray-900">{{ formattedTotalPrice }}</span>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <button
            class="btn-secondary"
            @click="$emit('add-another-product')"
        >
          <svg
              class="tw-h-5 tw-w-5 tw-mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
          >
            <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          Add Another Product
        </button>
        <button
            class="btn-primary"
            :style="{ backgroundColor: settings.primary_colour || '#10b981' }"
            @click="$emit('show-quote-form')"
        >
          <svg
              class="tw-h-5 tw-w-5 tw-mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
          >
            <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Request Quote
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ImageLayeringWithProps from "@/components/backend/pages/designer/components/List/ImageLayeringWithProps.vue";

export default {
  name: "LeadGeneratorQuote",
  components: {ImageLayeringWithProps},
  props: {
    quoteItems: {
      type: Array,
      required: true,
    },
    settings: {
      type: Object,
      required: true,
    },
  },
  computed: {
    totalQuantity() {
      return this.quoteItems.reduce((total, item) => total + (item.quantity || 1), 0);
    },
    totalPrice() {
      if (!this.quoteItems.some(item => item.price)) return null;
      return this.quoteItems.reduce((total, item) => {
        const price = this.parsePrice(item.price);
        const quantity = item.quantity || 1;
        return total + (price * quantity);
      }, 0);
    },
    formattedTotalPrice() {
      if (!this.totalPrice) return '';
      return this.formatPrice(this.totalPrice);
    }
  },
  watch: {
    quoteItems: {
      handler(newItems) {
        // If quote becomes empty, emit event to return to product selection
        if (newItems.length === 0) {
          this.$nextTick(() => {
            this.$emit('quote-emptied');
          });
        }
      },
      deep: true
    }
  },
  methods: {
    removeItem(index) {
      this.$emit("remove-from-quote", index);
    },
    parsePrice(price) {
      if (!price) return 0;
      if (typeof price === 'number') return price;
      // Remove currency symbols and parse
      return parseFloat(String(price).replace(/[^0-9.-]+/g, '')) || 0;
    },
    formatPrice(price) {
      if (!price && price !== 0) return '';
      if (typeof price === 'string' && price.includes('$')) return price;
      const numPrice = typeof price === 'number' ? price : this.parsePrice(price);
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(numPrice);
    }
  },
};
</script>

<style scoped>
/* Container Styles */
.quote-container {
  @apply tw-max-w-4xl tw-mx-auto tw-bg-white tw-min-h-screen;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* Header Section */
.quote-header {
  @apply tw-px-6 tw-pt-8 tw-pb-6;
  background: linear-gradient(to bottom, #f9fafb, #ffffff);
}

/* Quote Items */
.quote-items {
  @apply tw-px-6 tw-py-4;
}

.quote-item {
  @apply tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-p-6 tw-mb-4;
  @apply tw-transition-all tw-duration-200;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

.quote-item:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Image Container */
.item-image-container {
  @apply tw-w-32 tw-h-32 tw-flex-shrink-0 tw-bg-gray-50 tw-rounded-lg tw-overflow-hidden;
  @apply tw-flex tw-items-center tw-justify-center;
}

.item-image {
  @apply tw-w-full tw-h-full tw-object-contain;
}

/* Summary Section */
.quote-summary {
  @apply tw-mt-6 tw-mb-4;
}

/* Action Buttons */
.action-buttons {
  @apply tw-flex tw-gap-4 tw-mt-8 tw-pb-8;
  @apply tw-border-t tw-border-gray-200 tw-pt-6;
}

/* Button Styles */
.btn-primary,
.btn-secondary {
  @apply tw-flex tw-items-center tw-justify-center tw-px-6 tw-py-3 tw-rounded-lg;
  @apply tw-font-medium tw-text-base tw-transition-all tw-duration-200;
  @apply tw-flex-1;
}

.btn-primary {
  @apply tw-text-white tw-shadow-sm;
  background-color: var(--primary-color, #10b981);
}

.btn-primary:hover {
  @apply tw-shadow-md;
  filter: brightness(1.1);
}

.btn-secondary {
  @apply tw-bg-white tw-text-gray-700 tw-border tw-border-gray-300;
}

.btn-secondary:hover {
  @apply tw-bg-gray-50 tw-border-gray-400;
}

/* Responsive Design */
@media (max-width: 640px) {
  .quote-container {
    @apply tw-min-h-0;
  }

  .quote-header {
    @apply tw-px-4 tw-pt-6 tw-pb-4;
  }

  .quote-items {
    @apply tw-px-4;
  }

  .quote-item {
    @apply tw-p-4;
  }

  .item-image-container {
    @apply tw-w-24 tw-h-24;
  }

  .action-buttons {
    @apply tw-flex-col;
  }

  .btn-primary,
  .btn-secondary {
    @apply tw-w-full;
  }
}

/* Iframe-specific optimizations */
@media (max-width: 480px) {
  .quote-header h1 {
    @apply tw-text-xl;
  }

  .quote-item h3 {
    @apply tw-text-base;
  }
}

/* Animation for item removal */
.quote-item {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Print styles for quote printing */
@media print {
  .quote-container {
    @apply tw-max-w-none;
  }

  .btn-primary,
  .btn-secondary,
  button[aria-label="Remove item"] {
    display: none !important;
  }

  .quote-item {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}
</style>
